<template lang="pug">
.twin-image-container(v-if="hasFiles", v-loading="loading")
  .twin-image-primary
    img(:src="primaryImageSource", @click="showImageViewer(0)")

  .twin-image-secondary
    img(
      v-for="(src, index) in secondaryImageSources",
      :key="src",
      :src="src",
      @click="showImageViewer(index + 1)"
    )

  el-button.twin-image-add(
    v-if="!props.disabled",
    type="primary",
    circle,
    icon="Plus",
    @click.stop="openFileDialog"
  )

  .drag-overlay.absolute.top-0.left-0.right-0.bottom-0.bg-gray-500.flex.justify-center.items-center.p-16.text-xl(
    :class="{ 'opacity-50': isDragOver, 'opacity-0': !isDragOver }",
    @drop="onFileDrop",
    @dragover="onDragOver",
    @dragenter="onDragEnter",
    @dragleave="onDragLeave"
  ) {{ $t("dropFilesHere") }}

.empty-placeholder.cursor-pointer.twin-image-container(
  v-else,
  v-loading="loading",
  :style="{ 'border-color': props.disabled ? 'var(--color-grey-light)' : isDragOver ? 'var(--el-border-color-dark)' : 'var(--el-border-color)' }"
)
  .flex.flex-col.items-center.p-8(
    @click="openFileDialog",
    @drop="onFileDrop",
    @dragover="onDragOver",
    @dragenter="onDragEnter",
    @dragleave="onDragLeave"
  )
    el-icon(
      size="150",
      :color="props.disabled ? 'var(--color-grey-light)' : isDragOver ? 'var(--el-border-color-dark)' : 'var(--el-border-color)'"
    )
      Camera
    span.text-center(
      :style="{ color: props.disabled ? 'var(--color-grey-light)' : isDragOver ? 'var(--el-border-color-dark)' : 'var(--el-border-color)' }"
    ) {{ props.disabled ? $t("noImages") : $t("addImages") }}

input(
  ref="fileInput",
  type="file",
  hidden,
  accept="image/*",
  capture="environment",
  multiple,
  @change="uploadImage($event.target?.files)"
)
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import type { FileMetaData } from "../../services/documents";
import { useTwinDocumentsStore } from "../../stores/documents";
import { component as Viewer } from "v-viewer";
import { api as viewerApi } from "v-viewer";
import "viewerjs/dist/viewer.css";
import { ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";
import { stateInitial } from "@shared/models/state";
const documentsStore = useTwinDocumentsStore();

onMounted(() => {
  if (props.twinId) {
    documentsStore.loadTwinImages(props.twinId, false);
  }
});

const loadingState = computed(() =>
  props.twinId
    ? documentsStore.getTwinImageLoadingState(props.twinId)
    : { ...stateInitial },
);

const files = computed(() => {
  if (props.twinId) {
    return loadingState.value.result ?? [];
  }
  return props.modelValue;
});

const sortedFiles = computed(() => {
  return (files.value ?? []).toSorted((a, b) =>
    a.purpose === "primary" ? -1 : b.purpose === "primary" ? 1 : 0,
  );
});
const loading = computed(
  () => loadingState.value.loading || uploadLoading.value,
);
const loaded = computed(() => loadingState.value.loaded);
const error = computed(() => loadingState.value.error);

const uploadLoading = ref(false);

const fileInput = ref<HTMLElement | null>(null);

const props = defineProps<{
  // There are two ways to use this component,
  // either by supplying a twinId, in which case the upload will be made directly,
  // Or by setting the filereader model in which case no images will be uploaded, but only shown to the user
  twinId?: string;
  modelValue?: { id: string; file: File; purpose?: string }[];
  disabled?: boolean;
}>();

const hasFiles = computed(() => (files.value?.length ?? 0) > 0);

const isDragOver = ref(false);

function onFileDrop(event: DragEvent) {
  event.preventDefault();
  if (props.disabled) {
    return;
  }
  isDragOver.value = false;
  if(event.dataTransfer !== null) {
    uploadImage(event.dataTransfer.files);
  }

}

function onDragEnter(event: Event) {
  if (!isDragOver.value) {
    isDragOver.value = true;
  }
  event.preventDefault();
}

function onDragLeave(event: Event) {
  isDragOver.value = false;
  event.preventDefault();
}

function onDragOver(event: Event) {
  event.preventDefault();
}

function openFileDialog(event: Event) {
  if (props.disabled) {
    return;
  }
  fileInput.value!.click();
}

const { t } = useI18n();

const emit = defineEmits(["update:modelValue"]);

function uploadImage(fileList?: FileList) {
  if (fileList === undefined || fileList.length === 0) {
    return;
  }

  // Check it files are actually images
  for (const file of fileList) {
    if (!RegExp("^image/*").test(file.type)) {
      ElNotification({
        type: "error",
        title: t("error"),
        message: t("onlyImagesAreAllowed"),
      });
      return;
    }
  }

  if (props.twinId !== undefined) {
    uploadLoading.value = true;
    documentsStore.addTwinImages(props.twinId, fileList).finally(() => {
      uploadLoading.value = false;
    });
  } else {
    emit("update:modelValue", [
      ...props.modelValue,
      ...Array.from(fileList).map((file, index) => {
        return {
          id: Math.random(),
          file,
          purpose:
            props.modelValue?.length === 0 && index === 0 ? "primary" : null,
        };
      }),
    ]);
  }
}

const viewer = ref(null);

function showImageViewer(index: number) {
  const toolbar: any = {
    zoomIn: true,
    zoomOut: true,
    reset: true,
    oneToOne: true,
    play: false,
    prev: true,
    next: true,
    rotateLeft: true,
    rotateRight: true,
    flipHorizontal: true,
  };

  if (!props.disabled) {
    toolbar.setPrimary = async function () {
      if (props.twinId) {
        await documentsStore.setTwinImagePrimary(
          {
            entityId: props.twinId,
            entityType: "twin",
          },
          (viewer as any).image.alt,
        );
      } else {
        const m = [...props.modelValue];
        const oldPrimary = m.find((i) => i.purpose === "primary");
        if (oldPrimary) {
          oldPrimary.purpose = null;
        }

        const id = localImageData.value.find(
          (item) => item.data === (viewer as any).image.src,
        )?.id;

        const newPrimary = m.find((i) => i.id === id);
        if (newPrimary) {
          newPrimary.purpose = "primary";
        }
        emit("update:modelValue", m);
        viewer.destroy();
      }
    };

    toolbar.delete = async function (data) {
      if (props.twinId) {
        await documentsStore.deleteTwinImage(
          props.twinId,
          (viewer as any).image.alt,
        );
      } else {
        const id = localImageData.value.find(
          (item) => item.data === (viewer as any).image.src,
        )?.id;
        emit(
          "update:modelValue",
          props.modelValue.filter((item) => item.id !== id),
        );
      }
      // NOTE: viewer is wrongly typed
      viewer.destroy();
    };
  }

  const viewer = viewerApi({
    options: {
      toolbar,
      initialViewIndex: index,
    },
    images: props.twinId
      ? sortedFiles.value.map((item) => `/api/documents/${item.uuid}`)
      : localImageData.value.map((item) => item.data),
  });
}

async function loadLocalFiles() {
  if (props.twinId) {
    return;
  }
  const result = await Promise.all(
    sortedFiles.value.map((item) => {
      const existingData = localImageData.value.find((ed) => ed.id === item.id);
      if (existingData) {
        return existingData;
      }
      const temporaryFileReader = new FileReader();

      return new Promise((resolve, reject) => {
        temporaryFileReader.onerror = () => {
          temporaryFileReader.abort();
          reject(new DOMException("Problem parsing input file."));
        };

        temporaryFileReader.onload = () => {
          resolve({
            ...item,
            data: temporaryFileReader.result,
          });
        };
        temporaryFileReader.readAsDataURL(item.file);
      });
    }),
  );
  localImageData.value = result;
}

const localImageData = ref([]);

watch(sortedFiles, loadLocalFiles);

const primaryImageSource = computed(() => {
  if (props.twinId) {
    return `/api/documents/${sortedFiles.value[0].uuid}`;
  } else {
    return localImageData.value[0]?.data;
  }
});

const secondaryImageSources = computed(() => {
  if (props.twinId) {
    return sortedFiles.value
      .slice(1, 7)
      .map((file) => `/api/documents/${file.uuid}`);
  }
  return localImageData.value.slice(1, 7).map((item) => item.data);
});
</script>

<style>
.viewer-delete::before {
  /* NOTE: this is hacky, but the unfortunate decision of element plus to use non-font icosn makes it hard to use the icon here, so we use its svg directly */
  content: url("data:image/svg+xml,%3Csvg%20viewBox%3D%220%200%201024%201024%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20data-v-ea893728%3D%22%22%3E%3Cpath%20fill%3D%22white%22%20d%3D%22M160%20256H96a32%2032%200%200%201%200-64h256V95.936a32%2032%200%200%201%2032-32h256a32%2032%200%200%201%2032%2032V192h256a32%2032%200%201%201%200%2064h-64v672a32%2032%200%200%201-32%2032H192a32%2032%200%200%201-32-32V256zm448-64v-64H416v64h192zM224%20896h576V256H224v640zm192-128a32%2032%200%200%201-32-32V416a32%2032%200%200%201%2064%200v320a32%2032%200%200%201-32%2032zm192%200a32%2032%200%200%201-32-32V416a32%2032%200%200%201%2064%200v320a32%2032%200%200%201-32%2032z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E");
  display: block;
  padding-left: 2px;
  padding-top: 2px;
  max-width: 18px;
}

/* set primary shows a star outline */
.viewer-set-primary::before {
  /* NOTE: this is hacky, but the unfortunate decision of element plus to use non-font icosn makes it hard to use the icon here, so we use its svg directly */
  content: url("data:image/svg+xml,%3Csvg%20data-v-d2e47025%3D%22%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%201024%201024%22%3E%3Cpath%20fill%3D%22white%22%20d%3D%22m512%20747.84%20228.16%20119.936a6.4%206.4%200%200%200%209.28-6.72l-43.52-254.08%20184.512-179.904a6.4%206.4%200%200%200-3.52-10.88l-255.104-37.12L517.76%20147.904a6.4%206.4%200%200%200-11.52%200L392.192%20379.072l-255.104%2037.12a6.4%206.4%200%200%200-3.52%2010.88L318.08%20606.976l-43.584%20254.08a6.4%206.4%200%200%200%209.28%206.72zM313.6%20924.48a70.4%2070.4%200%200%201-102.144-74.24l37.888-220.928L88.96%20472.96A70.4%2070.4%200%200%201%20128%20352.896l221.76-32.256%2099.2-200.96a70.4%2070.4%200%200%201%20126.208%200l99.2%20200.96%20221.824%2032.256a70.4%2070.4%200%200%201%2039.04%20120.064L774.72%20629.376l37.888%20220.928a70.4%2070.4%200%200%201-102.144%2074.24L512%20820.096l-198.4%20104.32z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E");
  display: block;
  padding-left: 2px;
  padding-top: 2px;
  max-width: 18px;
}

/* is primary should show a filled star */
.viewer-is-primary::before {
  /* NOTE: this is hacky, but the unfortunate decision of element plus to use non-font icosn makes it hard to use the icon here, so we use its svg directly */
  content: url("data:image/svg+xml,%3Csvg%20data-v-d2e47025%3D%22%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%201024%201024%22%3E%3Cpath%20fill%3D%22white%22%20d%3D%22M283.84%20867.84%20512%20747.776l228.16%20119.936a6.4%206.4%200%200%200%209.28-6.72l-43.52-254.08%20184.512-179.904a6.4%206.4%200%200%200-3.52-10.88l-255.104-37.12L517.76%20147.904a6.4%206.4%200%200%200-11.52%200L392.192%20379.072l-255.104%2037.12a6.4%206.4%200%200%200-3.52%2010.88L318.08%20606.976l-43.584%20254.08a6.4%206.4%200%200%200%209.28%206.72z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E");
  display: block;
  padding-left: 2px;
  padding-top: 2px;
  max-width: 18px;
}
</style>

<style scoped>
.twin-image-container {
  position: relative;
  width: 100%;
  max-width: 400px;
  height: 308px;
  max-height: 100vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  display: grid;
  grid-template-columns: 1fr 32px;
  grid-template-rows: 1fr 50px;
  grid-row-gap: 8px;
  grid-column-gap: 8px;
  align-items: center;
}

.twin-image-primary {
  grid-area: 1 / 1 / 2 / 3;
}

.twin-image-primary>img {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.twin-image-add {
  grid-area: 2 / 2 / 3 / 3;
}

.twin-image-primary>img {
  object-fit: cover;
  height: 250px;
}

.twin-image-secondary {
  grid-area: 2 / 1 / 3 / 2;
  object-fit: contain;
  display: flex;
  max-height: 50px;
  max-width: 100%;
  overflow: hidden;
}

.twin-image-secondary>img {
  margin-right: 1rem;
  border: 2px solid;
  border-color: var(--el-border-color);
  object-fit: cover;
  width: 50px;
}

.drag-overlay {
  pointer-events: none;
}

.twin-image-grid .image-container {
  max-height: 100%;
  height: 100%;
  max-width: 100%;
}

.twin-image-grid img {
  max-height: 100%;
  height: 100%;
  width: 100%;
  max-width: 100%;
  object-fit: contain;
}

.empty-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  border-style: dashed;
  border-color: var(--el-border-color);
  color: var(--el-text-color-primary);
  border-width: 4px;
}
</style>
